import { computed, onMounted } from 'vue'
import { PermissionLevel, isMine } from '@src/utilities/userUtils'
import toEmailSettingsPayload from '@src/utilities/emailNotificationSettings'
import { INotificationsSettings } from '@src/types/notifications.types'
import userApi from '@src/api/user'
import { useRouter } from 'vue-router'
import useTracking from '@src/composables/tracking/tracking'
import { useCommunicationSettingsStore } from '@src/pinia/communicationSettings'
import { storeToRefs } from 'pinia'
import { useUserSettingsStore } from '@src/pinia/userSettings'
import { useUserStore } from '@src/pinia/user'
import { userGraphQlApi } from '@src/api/apiModuleInstances'

export default function useUser() {
  const router = useRouter()
  const { trackMixpanelEvent } = useTracking()

  const userStore = useUserStore()
  const {
    setUserImage,
    deleteUserImage,
    saveUserPartialUpdate,
    loadCurrentUser
  } = userStore
  const {
    user,
    roles,
    userFullName,
    imageError,
    permissionLevel,
    error,
    loading,
    isSuperAdmin,
    isContentAdmin,
    isNewUserThirtyDays,
    isNewUserSevenDays
  } = storeToRefs(userStore)

  const userSettingsStore = useUserSettingsStore()
  const {
    load: loadUserSettings,
    update: updateUserSettings
  } = userSettingsStore
  const {
    settings: userSettings,
    loading: userSettingsLoading
  } = storeToRefs(userSettingsStore)

  const language = computed(() => user.value.language)
  const currentUserId = computed(() => user.value.id)

  const isAdmin = computed(() => permissionLevel.value === PermissionLevel.Admin)

  function trackUserAvatarClick(userId: number) {
    trackMixpanelEvent('clickProfile', {
      is_own_profile: isMine(user.value, userId)
    })
  }

  async function goToUser(userId: number | string, options: { newTab?: boolean } = {}) {
    const numericId = typeof userId === 'number'
      ? userId
      : (await userApi.cached.getUser(userId)).data.id
    const path = `/user/${numericId}`
    if (options.newTab) {
      window.open(path, '_blank', 'noopener')
    } else {
      router.push({ path })
    }
    trackUserAvatarClick(numericId)
  }

  async function goToCurrentUser() {
    router.push('/user')
    trackUserAvatarClick(user.value.id)
  }

  async function setUtc() {
    const userUtcOffset = -(new Date().getTimezoneOffset())
    if (user.value.utc_offset_minutes !== userUtcOffset) {
      await userGraphQlApi.saveUser({ global_user_id: user.value.global_user_id, utc_offset_minutes: userUtcOffset })
    }
  }

  return {
    user,
    language,
    userSettings,
    roles,
    permissionLevel,
    isAdmin,
    imageError,
    isSuperAdmin,
    isContentAdmin,
    userLoading: loading,
    userSettingsLoading,
    isNewUserThirtyDays,
    isNewUserSevenDays,
    fullname: userFullName,
    userFetchError: error,
    currentUserId,
    loadCurrentUser,
    loadUserSettings,
    saveUserPartialUpdate,
    updateUserSettings,
    deleteUserImage,
    setUserImage,
    goToUser,
    setUtc,
    goToCurrentUser
  }
}

export function useUserNotificationSettings() {
  const communicationSettingsStore = useCommunicationSettingsStore()
  const {
    loadCommunicationSettings,
    updateEmailSettings,
    updateWebPushSettings
  } = communicationSettingsStore

  const {
    loading,
    updating,
    emailSettings,
    webPushSettings
  } = storeToRefs(communicationSettingsStore)

  onMounted(loadCommunicationSettings)

  function updateNotificationSettings(settings: INotificationsSettings) {
    updateEmailSettings(toEmailSettingsPayload(settings.email!))
    updateWebPushSettings(settings.webPush!)
  }

  return {
    loading,
    updating,
    emailSettings,
    webPushSettings,
    updateNotificationSettings
  }
}
